import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Camera, Check, ImagePlus, Plus, ShieldCheck, X } from 'lucide-react';
import { getAllProducts, getAllCategories, getCategory, getSubcategories, normalize } from '../../data/catalog';
import { addCustomCategory, addCustomProduct, addSubcategory, addVariantToFabric, getCustomSubcategories, saveProductOverride, slugify } from '../../utils/customProducts';
import { CoverAIGenerator } from '../../components/admin/CoverAIGenerator';
import { fileToDataUrl } from '../../utils/siteConfig';
import { VariantAIAnalysis } from '../../components/admin/VariantAIAnalysis';
import { PHOTO_STATUS, recordPhotoEvent, uploadImage } from '../../utils/photoStatus';
import { useQueue } from '../../context/QueueContext';

// Cadastro em lote por foto(s). A análise de variantes é REAL (visão via provedor de IA
// do painel): as fotos do conjunto são analisadas em conjunto e NUNCA viram foto pública
// de variante — a confirmação final é sempre do admin.

const STATUS_OPTIONS = [
  { key: 'DISPONIVEL', label: 'Disponível' },
  { key: 'TEMPORARIAMENTE_INDISPONIVEL', label: 'Temporariamente indisponível' },
  { key: 'REVISAR', label: 'Revisar' },
];
const STEPS = ['Tecido', 'Foto', 'Variantes', 'Duplicidade', 'Fotos', 'Revisão'];
const STEP_KEYS = ['dados', 'foto', 'variantes', 'duplicidade', 'fotos', 'revisao'];

const inputCls =
  'mt-1 h-12 w-full rounded-xl border border-input bg-white px-4 text-sm outline-none focus:border-navy focus:ring-2 focus:ring-navy/15';
const labelCls = 'block text-xs font-semibold uppercase tracking-wide text-muted-foreground';
const nextBtn =
  'flex w-full items-center justify-center gap-2 rounded-full bg-navy px-8 py-4 text-sm font-semibold tracking-wide text-white hover:bg-navy-hover disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto';

const activeVariants = (list) => list.filter((v) => !v.excluded && !v.notVariant && v.dupe !== 'drop');

// Nome idêntico (normalizado): único caso em que se considera "o mesmo produto".
function findExactFabric(name) {
  const norm = normalize(name.trim());
  if (!norm) return null;
  return getAllProducts().find((p) => normalize(p.name) === norm) || null;
}

// Nomes parecidos (contenção de tokens em qualquer direção, ex.: "Brim" ⊂ "Brim Misto")
// servem apenas para AVISAR o operador — nunca para vincular variantes automaticamente.
function findSimilarFabrics(name) {
  const norm = normalize(name.trim());
  const tokens = norm.split(/\s+/).filter((t) => t.length > 2);
  if (!tokens.length) return [];
  return getAllProducts().filter((p) => {
    const pn = normalize(p.name);
    if (pn === norm) return false;
    const ptoks = pn.split(/\s+/).filter((t) => t.length > 2);
    if (!ptoks.length) return false;
    return ptoks.every((t) => tokens.includes(t)) || tokens.every((t) => ptoks.includes(t));
  });
}

function variantDupe(name, product) {
  if (!product) return null;
  const tokens = normalize(name).split(/\s+/).filter((t) => t.length > 2);
  if (!tokens.length) return null;
  return (
    product.variants.find((v) => {
      const vn = normalize(v.name);
      return tokens.every((t) => vn.includes(t));
    }) || null
  );
}

export default function AdminBatchProduct() {
  const { user } = useQueue();
  const navigate = useNavigate();
  const [step, setStep] = useState('dados');
  const [fabric, setFabric] = useState({
    categorySlug: 'alfaiataria',
    subcategorySlug: '',
    name: '',
    width: '',
    composition: '',
    gramatura: '',
    elasticidade: '',
    unidadeVenda: 'Metro',
    fabricante: '',
    aiNotes: '',
  });
  const [variants, setVariants] = useState([]);
  // Destino das variantes: null = ainda não escolhido; o sistema nunca decide sozinho
  // entre criar produto novo e anexar a um existente — sempre escolha explícita.
  const [attachChoice, setAttachChoice] = useState(null);
  const [attachSlug, setAttachSlug] = useState('');
  const [desc, setDesc] = useState('');
  const [coverAI, setCoverAI] = useState(null);
  // Classificações NOVAS digitadas no fluxo: vivem só no estado até APROVAR E CADASTRAR.
  // Cancelar ou sair sem publicar não deixa categoria/subcategoria órfã.
  const [pendingCategory, setPendingCategory] = useState(null);
  const [pendingSub, setPendingSub] = useState(null);
  const pendingCategoryDup =
    pendingCategory !== null && !!pendingCategory.trim() && getAllCategories().some((c) => c.slug === slugify(pendingCategory.trim()));
  const pendingSubDup =
    pendingSub !== null && !!pendingSub.trim() && getCustomSubcategories().some((s) => s.slug === slugify(pendingSub.trim()));
  const pendingInvalid =
    (pendingCategory !== null && !pendingCategory.trim()) ||
    (pendingSub !== null && !pendingSub.trim()) ||
    pendingCategoryDup ||
    pendingSubDup;
  const [status, setStatus] = useState('DISPONIVEL');
  const [suggest, setSuggest] = useState(null);
  const [publishedSlug, setPublishedSlug] = useState(null);
  const [publishedCount, setPublishedCount] = useState(0);
  const [error, setError] = useState('');
  const [publishing, setPublishing] = useState(false);

  const set = (k) => (e) => setFabric((f) => ({ ...f, [k]: e.target.value }));
  const patchVariant = (key, patch) => setVariants((l) => l.map((v) => (v.key === key ? { ...v, ...patch } : v)));
  const kept = activeVariants(variants);

  const exactFabric = fabric.name.trim() ? findExactFabric(fabric.name) : null;
  const similarFabrics = fabric.name.trim() && !exactFabric ? findSimilarFabrics(fabric.name) : [];
  const needsDestChoice = !!(exactFabric || similarFabrics.length);
  const targetProduct =
    attachChoice === 'existing' ? (attachSlug ? getAllProducts().find((p) => p.slug === attachSlug) || null : exactFabric) : null;
  const pendingDupes = kept.filter((v) => v.dupe === 'pending');

  // Recalcula duplicidades de variantes contra o produto escolhido como destino.
  const recheckDupes = (product) =>
    setVariants((l) =>
      l.map((v) => {
        if (v.excluded || v.notVariant) return v;
        const dup = product ? variantDupe(v.name, product) : null;
        return { ...v, dupe: dup ? 'pending' : null };
      })
    );

  // As variantes confirmadas na análise compartilhada (VariantAIAnalysis) entram no
  // fluxo do lote e seguem para a verificação de duplicidade/destino.
  const confirmVariantAnalysis = (list) => {
    const mapped = list.map((v, i) => ({
      key: v.key || `v${i + 1}`,
      name: v.name,
      pattern: v.pattern,
      code: v.code || '',
      image: null,
      individual: false,
      excluded: false,
      notVariant: false,
      dupe: null,
      photoIndexes: v.photoIndexes || [],
      confidence: v.confidence || 'media',
      visualNote: v.visualNote || '',
      manual: !!v.manual,
    }));
    runDuplicity(mapped);
  };

  const runDuplicity = (source) => {
    setError('');
    const list = source || kept;
    if (list.some((v) => !v.name.trim())) {
      setError('Toda variante ativa precisa de um nome. Edite, exclua ou marque como “não é variante”.');
      return;
    }
    setVariants(
      list.map((v) => {
        if (v.excluded || v.notVariant) return v;
        const dup = variantDupe(v.name, targetProduct);
        return { ...v, dupe: dup ? v.dupe || 'pending' : null };
      })
    );
    setStep('duplicidade');
  };

  const uploadVariantPhoto = async (e, key) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const dataUrl = await fileToDataUrl(file, 1200);
    patchVariant(key, { image: dataUrl, individual: true });
    e.target.value = '';
  };

  const uploadGlobalPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const dataUrl = await fileToDataUrl(file, 1200);
    const target = kept.find((v) => !v.individual) || kept[0];
    if (target) setSuggest({ dataUrl, key: target.key, name: target.name });
    e.target.value = '';
  };

  const generateDesc = () => {
    const notas = fabric.aiNotes.trim();
    setDesc(
      `${fabric.name} é um tecido da categoria ${getCategory(fabric.categorySlug)?.name || ''}` +
        `${fabric.composition ? `, em ${fabric.composition}` : ''}${fabric.width ? `, com largura de ${fabric.width}` : ''}. ` +
        (notas ? `${notas.charAt(0).toUpperCase() + notas.slice(1)}. ` : '') +
        'Confira as variantes disponíveis e informe a metragem desejada na sua solicitação.'
    );
  };

  const uniqueSlug = (name) => {
    const base = slugify(name) || `tecido-${Date.now()}`;
    const all = getAllProducts();
    let slug = base;
    let n = 2;
    while (all.some((p) => p.slug === slug)) {
      slug = `${base}-${n}`;
      n += 1;
    }
    return slug;
  };

  const uniqueVariantId = (product, name) => {
    const base = slugify(name) || `v${Date.now()}`;
    let id = base;
    let n = 2;
    while (product.variants.some((v) => v.id === id)) {
      id = `${base}-${n}`;
      n += 1;
    }
    return id;
  };

  const publish = async () => {
    if (pendingInvalid || publishing) return;
    setError('');
    setPublishing(true);
    try {
      // Classificações novas (etapa 1) só são efetivadas AQUI, na publicação final.
      // Ao adicionar variantes a um tecido existente, a categoria dele é preservada
      // e nenhuma classificação nova é criada (evita órfãs).
      let finalCategorySlug = fabric.categorySlug;
      let finalSubSlug = getSubcategories(fabric.categorySlug).some((s) => s.slug === fabric.subcategorySlug) ? fabric.subcategorySlug : undefined;
      if (!targetProduct && pendingCategory !== null) {
        finalCategorySlug = slugify(pendingCategory.trim());
        await addCustomCategory({ slug: finalCategorySlug, name: pendingCategory.trim(), tagline: '', description: '', custom: true });
        finalSubSlug = undefined;
      }
      if (!targetProduct && pendingSub !== null) {
        finalSubSlug = slugify(pendingSub.trim());
        await addSubcategory({ slug: finalSubSlug, categorySlug: finalCategorySlug, name: pendingSub.trim() });
      }
      // Fotos reais sobem para o armazenamento durável do servidor; as fotos do conjunto
      // (material de análise da IA) NUNCA viram foto pública de variante — sem foto
      // individual, a variante fica FOTO_PENDENTE com placeholder público.
      const resolveVariantImg = async (v) => {
        if (v.image) return v.image.startsWith('data:') ? uploadImage(v.image, `lote/${slugify(fabric.name)}`) : v.image;
        return null;
      };
      const buildVariant = async (v, product) => ({
        id: product ? uniqueVariantId(product, v.name) : slugify(v.name) || `v${Date.now()}`,
        name: v.name.trim(),
        code: v.code.trim() || undefined,
        pattern: v.pattern,
        image: await resolveVariantImg(v),
        description: desc,
        status,
        statusHistory: [],
        photoStatus: v.individual ? PHOTO_STATUS.APROVADA : PHOTO_STATUS.PENDENTE,
        createdAt: new Date().toISOString(),
        createdBy: user?.name,
      });
      let slug;
      if (targetProduct) {
        for (const v of kept) {
          const built = await buildVariant(v, targetProduct);
          await addVariantToFabric(targetProduct.slug, built);
          if (!v.individual) {
            recordPhotoEvent({
              at: new Date().toISOString(),
              userId: user?.id,
              userName: user?.name,
              action: `Variante “${built.name}” cadastrada com imagem provisória — foto original pendente.`,
              produto: targetProduct.name,
              variante: built.id,
              origem: 'FOTOS',
            });
          }
        }
        if (coverAI) await saveProductOverride(targetProduct.slug, { coverImage: coverAI });
        slug = targetProduct.slug;
      } else {
        slug = uniqueSlug(fabric.name);
        const newVariants = [];
        for (const v of kept) newVariants.push(await buildVariant(v, null));
        await addCustomProduct({
          slug,
          name: fabric.name.trim(),
          categorySlug: finalCategorySlug,
          subcategorySlug: finalSubSlug,
          hasVariants: true,
          coverImage: coverAI || undefined,
          description: desc,
          composition: fabric.composition || 'Informação não preenchida',
          width: fabric.width || 'Informação não preenchida',
          gramatura: fabric.gramatura || undefined,
          elasticidade: fabric.elasticidade || undefined,
          unidadeVenda: fabric.unidadeVenda || undefined,
          fabricante: fabric.fabricante || undefined,
          colorTags: [],
          usageTags: [],
          keywords: [fabric.name, ...kept.map((v) => v.name)].filter(Boolean),
          featured: false,
          variants: newVariants,
        });
        newVariants.forEach((built, i) => {
          if (!kept[i].individual) {
            recordPhotoEvent({
              at: new Date().toISOString(),
              userId: user?.id,
              userName: user?.name,
              action: `Variante “${built.name}” cadastrada com imagem provisória — foto original pendente.`,
              produto: fabric.name.trim(),
              variante: built.id,
              origem: 'FOTOS',
            });
          }
        });
      }
      // Histórico administrativo do lote: persistido no servidor (auditoria).
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/batch-imports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Role': user?.role || '' },
        body: JSON.stringify({
          at: new Date().toISOString(),
          userId: user?.id,
          userName: user?.name,
          tecido: fabric.name,
          variantes: kept.length,
          destino: targetProduct ? `existente:${targetProduct.slug}` : `novo:${slug}`,
          status,
          origem: 'CADASTRO EM LOTE POR FOTO (IA REAL)',
        }),
      }).catch((e) => console.error('Histórico do lote: falha ao gravar no servidor.', e));
      setPublishedSlug(slug);
      setPublishedCount(kept.length);
      setStep('publicado');
    } catch (e) {
      setError(e.message || 'Não foi possível cadastrar. Verifique sua conexão e tente novamente.');
    } finally {
      setPublishing(false);
    }
  };

  if (step === 'publicado') {
    return (
      <div className="mx-auto max-w-2xl rounded-2xl border border-navy/10 bg-white p-8 text-center" data-testid="batch-published">
        <ShieldCheck className="mx-auto h-12 w-12 text-emerald-600" strokeWidth={1.5} />
        <h2 className="mt-4 font-serif text-2xl text-navy">Cadastro em lote concluído</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {publishedCount} variante(s) de “{fabric.name}” cadastradas com status{' '}
          {STATUS_OPTIONS.find((s) => s.key === status)?.label}. Gravado no servidor (banco de dados) —
          visível em qualquer dispositivo.
        </p>
        <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
          {publishedSlug && (
            <Link to={`/produto/${publishedSlug}`} className="rounded-full bg-navy px-7 py-3 text-sm font-semibold text-white hover:bg-navy-hover" data-testid="batch-published-view">
              VER NO CATÁLOGO
            </Link>
          )}
          <button onClick={() => navigate('/admin/produtos')} className="rounded-full border border-navy/20 px-7 py-3 text-sm font-semibold text-navy hover:bg-navy-light" data-testid="batch-published-back">
            VOLTAR AOS PRODUTOS
          </button>
        </div>
      </div>
    );
  }

  const stepIdx = STEP_KEYS.indexOf(step);

  return (
    <div className="mx-auto max-w-3xl" data-testid="batch-page">
      <Link to="/admin/produtos/novo" className="inline-flex items-center gap-1 text-sm font-semibold text-navy hover:underline" data-testid="batch-back">
        <ArrowLeft className="h-4 w-4" /> Voltar
      </Link>
      <h2 className="mt-3 font-serif text-2xl text-navy">Cadastro em Lote por Foto</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Fotografe várias variantes do mesmo tecido em uma única foto. A análise de IA é
        <strong> feita de verdade pela IA de visão</strong> (provedor configurado em Integrações de IA) — nada é publicado sem sua aprovação.
      </p>

      <ol className="no-scrollbar -mx-4 mt-5 flex items-center gap-2 overflow-x-auto px-4 pb-1 text-[11px] font-semibold uppercase tracking-wide" data-testid="batch-steps">
        {STEPS.map((label, i) => (
          <li key={label} className="flex shrink-0 items-center gap-2">
            <span className={`flex h-6 w-6 items-center justify-center rounded-full ${stepIdx >= i ? 'bg-navy text-white' : 'bg-muted text-muted-foreground'}`}>{i + 1}</span>
            <span className={stepIdx >= i ? 'text-navy' : 'text-muted-foreground'}>{label}</span>
            {i < STEPS.length - 1 && <span className="text-muted-foreground">→</span>}
          </li>
        ))}
      </ol>

      {error && (
        <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700" role="alert" data-testid="batch-error">{error}</p>
      )}

      {step === 'dados' && (
        <section className="mt-6 rounded-2xl border border-navy/10 bg-white p-5" data-testid="batch-step-dados">
          <h3 className="font-serif text-lg text-navy">Etapa 1 — Dados do tecido</h3>
          <p className="mt-1 text-xs text-muted-foreground">Estes dados serão reutilizados em todas as variantes criadas.</p>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="batch-category" className={labelCls}>Categoria</label>
              <select
                id="batch-category"
                data-testid="batch-category"
                value={pendingCategory !== null ? '__nova__' : fabric.categorySlug}
                onChange={(e) => {
                  if (e.target.value === '__nova__') setPendingCategory('');
                  else setPendingCategory(null);
                }}
                className={inputCls}
              >
                {getAllCategories().map((c) => (
                  <option key={c.slug} value={c.slug}>{c.name}</option>
                ))}
                <option value="__nova__" data-testid="batch-category-new-option">+ Criar nova categoria</option>
              </select>
              {pendingCategory === null && (
                <input type="hidden" data-testid="batch-category-current" value={fabric.categorySlug} readOnly />
              )}
              {pendingCategory !== null && (
                <>
                  <input
                    value={pendingCategory}
                    onChange={(e) => setPendingCategory(e.target.value)}
                    placeholder="Ex.: Religiosos"
                    aria-label="Nome da nova categoria"
                    data-testid="batch-new-category-input"
                    className={inputCls}
                  />
                  {pendingCategoryDup && (
                    <p className="mt-1 text-[11px] font-medium text-red-700" data-testid="batch-new-category-dup">Já existe uma categoria com este nome.</p>
                  )}
                  <p className="mt-1 text-[11px] text-muted-foreground">A categoria só será criada ao aprovar o cadastro na etapa final.</p>
                </>
              )}
            </div>
            <div>
              <label htmlFor="batch-subcategory" className={labelCls}>Subcategoria (opcional)</label>
              <select
                id="batch-subcategory"
                data-testid="batch-subcategory"
                value={pendingSub !== null ? '__nova__' : fabric.subcategorySlug || ''}
                onChange={(e) => {
                  if (e.target.value === '__nova__') setPendingSub('');
                  else {
                    setPendingSub(null);
                    set('subcategorySlug')(e);
                  }
                }}
                className={inputCls}
              >
                <option value="">Sem subcategoria</option>
                {(pendingCategory !== null ? [] : getSubcategories(fabric.categorySlug)).map((s) => (
                  <option key={s.slug} value={s.slug}>{s.name}</option>
                ))}
                <option value="__nova__" data-testid="batch-subcategory-new-option">+ Criar nova subcategoria</option>
              </select>
              {pendingSub !== null && (
                <>
                  <input
                    value={pendingSub}
                    onChange={(e) => setPendingSub(e.target.value)}
                    placeholder="Ex.: Litúrgicos"
                    aria-label="Nome da nova subcategoria"
                    data-testid="batch-new-subcategory-input"
                    className={inputCls}
                  />
                  {pendingSubDup && (
                    <p className="mt-1 text-[11px] font-medium text-red-700" data-testid="batch-new-subcategory-dup">Já existe uma subcategoria com este nome.</p>
                  )}
                  <p className="mt-1 text-[11px] text-muted-foreground">A subcategoria só será criada ao aprovar o cadastro na etapa final.</p>
                </>
              )}
            </div>
            <div>
              <label htmlFor="batch-name" className={labelCls}>Nome do tecido *</label>
              <input id="batch-name" data-testid="batch-name" value={fabric.name} onChange={(e) => { set('name')(e); setAttachChoice(null); setAttachSlug(''); }} className={inputCls} placeholder="Ex.: Cetim Jacquard" />
              {needsDestChoice && (
                <p className="mt-1 text-[11px] font-medium text-amber-700" data-testid="batch-name-conflict-hint">
                  {exactFabric
                    ? 'Já existe um produto com este nome — você escolherá o destino das variantes na etapa 4.'
                    : `Nome parecido com ${similarFabrics.map((p) => `“${p.name}”`).join(', ')} — você escolherá o destino na etapa 4.`}
                </p>
              )}
            </div>
            <div>
              <label htmlFor="batch-width" className={labelCls}>Largura</label>
              <input id="batch-width" data-testid="batch-width" value={fabric.width} onChange={set('width')} className={inputCls} placeholder="Ex.: 1,50 m" />
            </div>
            <div>
              <label htmlFor="batch-composition" className={labelCls}>Composição</label>
              <input id="batch-composition" data-testid="batch-composition" value={fabric.composition} onChange={set('composition')} className={inputCls} placeholder="Ex.: 100% poliéster" />
            </div>
            <div>
              <label htmlFor="batch-gramatura" className={labelCls}>Gramatura (opcional)</label>
              <input id="batch-gramatura" data-testid="batch-gramatura" value={fabric.gramatura} onChange={set('gramatura')} className={inputCls} placeholder="Ex.: 180 g/m²" />
            </div>
            <div>
              <label htmlFor="batch-elasticidade" className={labelCls}>Elasticidade (opcional)</label>
              <input id="batch-elasticidade" data-testid="batch-elasticidade" value={fabric.elasticidade} onChange={set('elasticidade')} className={inputCls} placeholder="Ex.: sem elasticidade" />
            </div>
            <div>
              <label htmlFor="batch-unit" className={labelCls}>Unidade de venda</label>
              <select id="batch-unit" data-testid="batch-unit" value={fabric.unidadeVenda} onChange={set('unidadeVenda')} className={inputCls}>
                <option>Metro</option>
                <option>Peça</option>
                <option>Retalho</option>
              </select>
            </div>
            <div>
              <label htmlFor="batch-manufacturer" className={labelCls}>Fabricante / Linha (opcional)</label>
              <input id="batch-manufacturer" data-testid="batch-manufacturer" value={fabric.fabricante} onChange={set('fabricante')} className={inputCls} placeholder="Ex.: Linha Premium" />
            </div>
          </div>
          <div className="mt-4">
            <label htmlFor="batch-ai-notes" className={labelCls}>Observações para a IA (opcional)</label>
            <textarea id="batch-ai-notes" data-testid="batch-ai-notes" value={fabric.aiNotes} onChange={set('aiNotes')} rows={2} className="mt-1 w-full rounded-xl border border-input bg-white px-4 py-3 text-sm outline-none focus:border-navy" placeholder="Ex.: leve, não amassa facilmente, possui brilho" />
          </div>
          <button
            type="button"
            onClick={() => {
              setError('');
              if (!fabric.name.trim()) {
                setError('Informe o nome do tecido para continuar.');
                return;
              }
              setStep('foto');
            }}
            data-testid="batch-next-dados"
            disabled={pendingInvalid}
            className={`mt-5 ${nextBtn} disabled:cursor-not-allowed disabled:opacity-40`}
          >
            CONTINUAR <ArrowRight className="h-4 w-4" />
          </button>
          {pendingInvalid && (
            <p className="mt-2 text-xs font-medium text-amber-800" data-testid="batch-pending-classification-error">
              Complete o nome da nova categoria/subcategoria (ou escolha uma existente) para continuar.
            </p>
          )}
        </section>
      )}

      {/* Análise de variantes por múltiplas fotos — implementação compartilhada
          (components/admin/VariantAIAnalysis.jsx), também usada na edição de produto.
          Fica montada (oculta) nas demais etapas para não perder fotos/revisão. */}
      <div className={step === 'foto' || step === 'variantes' ? '' : 'hidden'}>
        <VariantAIAnalysis
          testidPrefix="batch"
          productName={fabric.name}
          notes={fabric.aiNotes}
          existingVariants={[]}
          confirmLabel="CONTINUAR"
          confirmTestid="batch-next-variantes"
          fotoTitle="Etapa 2 — Fotos do conjunto"
          reviewTitle="Etapa 3 — Variantes detectadas"
          onBack={() => setStep('dados')}
          onConfirm={confirmVariantAnalysis}
        />
      </div>

      {step === 'duplicidade' && (
        <section className="mt-6 rounded-2xl border border-navy/10 bg-white p-5" data-testid="batch-step-duplicidade">
          <h3 className="font-serif text-lg text-navy">Etapa 4 — Verificação de duplicidade</h3>
          {needsDestChoice ? (
            <div className="mt-3 rounded-xl border border-amber-400 bg-amber-50 p-4" data-testid="batch-dupe-fabric-notice">
              <p className="text-sm font-semibold text-amber-800">
                {exactFabric
                  ? `Já existe um produto com este nome: ${exactFabric.name}.`
                  : `Nome parecido com ${similarFabrics.length > 1 ? 'produtos existentes' : 'produto existente'}: ${similarFabrics.map((p) => p.name).join(', ')}.`}
              </p>
              <p className="mt-1 text-xs text-amber-800/80">O sistema não decide sozinho — escolha o destino das variantes:</p>
              <div className="mt-3 space-y-2">
                <label className="flex items-start gap-3 rounded-xl border border-navy/15 bg-white p-3 text-sm">
                  <input type="radio" name="attach" checked={attachChoice === 'new'} onChange={() => { setAttachChoice('new'); setAttachSlug(''); recheckDupes(null); }} className="mt-0.5 h-4 w-4 accent-navy" data-testid="batch-create-new" />
                  <span><strong>Criar o produto novo “{fabric.name.trim()}”.</strong> O nome digitado é preservado por inteiro — nada vira variante de outro produto.</span>
                </label>
                {exactFabric && (
                  <label className="flex items-start gap-3 rounded-xl border border-navy/15 bg-white p-3 text-sm">
                    <input type="radio" name="attach" checked={attachChoice === 'existing' && !attachSlug} onChange={() => { setAttachChoice('existing'); setAttachSlug(''); recheckDupes(exactFabric); }} className="mt-0.5 h-4 w-4 accent-navy" data-testid="batch-attach-existing" />
                    <span><strong>Adicionar as variantes a “{exactFabric.name}”.</strong> Não será criado outro produto.</span>
                  </label>
                )}
                {similarFabrics.map((p) => (
                  <label key={p.slug} className="flex items-start gap-3 rounded-xl border border-navy/15 bg-white p-3 text-sm">
                    <input type="radio" name="attach" checked={attachChoice === 'existing' && attachSlug === p.slug} onChange={() => { setAttachChoice('existing'); setAttachSlug(p.slug); recheckDupes(p); }} className="mt-0.5 h-4 w-4 accent-navy" data-testid={`batch-attach-similar-${p.slug}`} />
                    <span><strong>Adicionar as variantes a “{p.name}”.</strong> Não será criado outro produto.</span>
                  </label>
                ))}
              </div>
              {attachChoice === null && (
                <p className="mt-3 text-xs font-semibold text-amber-700" data-testid="batch-dest-choice-required">Escolha uma das opções acima para continuar.</p>
              )}
            </div>
          ) : (
            <p className="mt-3 rounded-xl bg-emerald-50 p-4 text-sm font-medium text-emerald-800" data-testid="batch-no-dupe">
              Nenhum produto existente com este nome. Será criado um novo tecido.
            </p>
          )}

          <div className="mt-4 space-y-2">
            {kept.map((v) =>
              v.dupe ? (
                <div key={v.key} className="rounded-xl border border-amber-400 bg-amber-50 p-3" data-testid={`batch-variant-dupe-${v.key}`}>
                  <p className="text-sm font-semibold text-amber-800">Possível variante existente: {v.name}</p>
                  {v.dupe === 'pending' ? (
                    <div className="mt-2 flex gap-2">
                      <button type="button" onClick={() => patchVariant(v.key, { dupe: 'keep' })} data-testid={`batch-dupe-keep-${v.key}`} className="flex-1 rounded-full bg-navy px-4 py-2.5 text-xs font-bold text-white">
                        NÃO É DUPLICADA — MANTER
                      </button>
                      <button type="button" onClick={() => patchVariant(v.key, { dupe: 'drop' })} data-testid={`batch-dupe-drop-${v.key}`} className="flex-1 rounded-full border border-red-300 px-4 py-2.5 text-xs font-bold text-red-700">
                        EXCLUIR
                      </button>
                    </div>
                  ) : (
                    <p className="mt-1 text-xs font-medium text-amber-700" data-testid={`batch-dupe-decided-${v.key}`}>
                      Decisão registrada: {v.dupe === 'keep' ? 'mantida (não é duplicada)' : 'excluída'}.
                    </p>
                  )}
                </div>
              ) : null
            )}
          </div>

          {pendingDupes.length > 0 && (
            <p className="mt-3 text-xs font-semibold text-amber-700" data-testid="batch-dupe-pending-note">
              Resolva os alertas de duplicidade acima para continuar.
            </p>
          )}
          <div className="mt-5 flex flex-col gap-3 sm:flex-row">
            <button type="button" onClick={() => setStep('variantes')} className="flex items-center justify-center gap-2 rounded-full border border-navy/20 px-6 py-3.5 text-sm font-semibold text-navy hover:bg-navy-light" data-testid="batch-back-variantes">
              <ArrowLeft className="h-4 w-4" /> VOLTAR
            </button>
            <button type="button" onClick={() => setStep('fotos')} disabled={pendingDupes.length > 0 || (needsDestChoice && attachChoice === null)} data-testid="batch-next-duplicidade" className={`flex-1 ${nextBtn} sm:flex-none`}>
              CONTINUAR <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </section>
      )}

      {step === 'fotos' && (
        <section className="mt-6" data-testid="batch-step-fotos">
          <h3 className="font-serif text-lg text-navy">Etapa 5 — Fotografias individuais (podem ser adicionadas agora ou depois)</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            A foto do conjunto é apenas para identificação. Adicione fotos individuais das variantes — a IA sugere
            a correspondência e você confirma. A cor real do tecido nunca é alterada.
          </p>
          <label htmlFor="batch-photo-global" data-testid="batch-photo-global-label" className="mt-4 flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-navy/25 bg-navy-light/30 px-4 py-4 text-sm font-semibold text-navy hover:bg-navy-light">
            <ImagePlus className="h-5 w-5" /> ENVIAR FOTO E DEIXAR A IA SUGERIR A VARIANTE
          </label>
          <input id="batch-photo-global" data-testid="batch-photo-global" type="file" accept="image/*" capture="environment" className="sr-only" onChange={uploadGlobalPhoto} />

          <div className="mt-4 space-y-3">
            {kept.map((v) => (
              <div key={v.key} className="flex items-center gap-3 rounded-2xl border border-navy/10 bg-white p-3" data-testid={`batch-photo-row-${v.key}`}>
                {v.image && <img src={v.image} alt={v.name} className="h-14 w-14 shrink-0 rounded-xl object-cover" />}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{v.name}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {v.individual ? 'Foto própria' : v.photoIndexes?.length ? `Sem foto — identificada na(s) foto(s) ${v.photoIndexes.map((i) => i + 1).join(', ')} do conjunto` : 'Sem foto — adicione quando puder'}
                  </p>
                </div>
                <label htmlFor={`batch-photo-${v.key}`} data-testid={`batch-photo-add-${v.key}`} className="flex h-11 shrink-0 cursor-pointer items-center gap-2 rounded-xl border border-navy/20 px-4 text-xs font-semibold text-navy hover:bg-navy-light">
                  <Camera className="h-4 w-4" /> {v.individual ? 'TROCAR' : 'ADICIONAR FOTO'}
                </label>
                <input id={`batch-photo-${v.key}`} type="file" accept="image/*" capture="environment" className="sr-only" onChange={(e) => uploadVariantPhoto(e, v.key)} />
              </div>
            ))}
          </div>

          <div className="mt-5 flex flex-col gap-3 sm:flex-row">
            <button type="button" onClick={() => setStep('duplicidade')} className="flex items-center justify-center gap-2 rounded-full border border-navy/20 px-6 py-3.5 text-sm font-semibold text-navy hover:bg-navy-light" data-testid="batch-back-duplicidade">
              <ArrowLeft className="h-4 w-4" /> VOLTAR
            </button>
            <button type="button" onClick={() => setStep('revisao')} data-testid="batch-next-fotos" className={`flex-1 ${nextBtn} sm:flex-none`}>
              CONTINUAR <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </section>
      )}

      {step === 'revisao' && (
        <section className="mt-6 space-y-4" data-testid="batch-step-revisao">
          <h3 className="font-serif text-lg text-navy">Etapa 6 — Revisão</h3>
          <div className="rounded-2xl border border-navy/10 bg-white p-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Tecido</p>
            <p className="mt-1 font-medium text-navy" data-testid="batch-review-fabric">{fabric.name}</p>
            <dl className="mt-2 grid grid-cols-2 gap-2 text-xs">
              <div><dt className="text-muted-foreground">Categoria</dt><dd className="font-medium" data-testid="batch-review-categoria">{pendingCategory !== null ? `${pendingCategory.trim() || 'Nova categoria'} (nova)` : getCategory(fabric.categorySlug)?.name}</dd></div>
              <div><dt className="text-muted-foreground">Subcategoria</dt><dd className="font-medium" data-testid="batch-review-subcategoria">{pendingSub !== null ? `${pendingSub.trim() || 'Nova subcategoria'} (nova)` : (getSubcategories(fabric.categorySlug).find((s) => s.slug === fabric.subcategorySlug)?.name || '—')}</dd></div>
              <div><dt className="text-muted-foreground">Largura</dt><dd className="font-medium">{fabric.width || '—'}</dd></div>
              <div><dt className="text-muted-foreground">Composição</dt><dd className="font-medium">{fabric.composition || '—'}</dd></div>
              <div><dt className="text-muted-foreground">Gramatura</dt><dd className="font-medium">{fabric.gramatura || '—'}</dd></div>
              <div><dt className="text-muted-foreground">Elasticidade</dt><dd className="font-medium">{fabric.elasticidade || '—'}</dd></div>
              <div><dt className="text-muted-foreground">Unidade</dt><dd className="font-medium">{fabric.unidadeVenda}</dd></div>
              <div><dt className="text-muted-foreground">Fabricante/Linha</dt><dd className="font-medium">{fabric.fabricante || '—'}</dd></div>
              <div><dt className="text-muted-foreground">Destino</dt><dd className="font-medium" data-testid="batch-review-destino">{targetProduct ? `Adicionar a: ${targetProduct.name}` : 'Novo tecido'}</dd></div>
            </dl>
            <div className="mt-3 flex items-center justify-between gap-2">
              <label htmlFor="batch-description" className={labelCls}>Descrição sugerida pela IA</label>
              <button type="button" onClick={generateDesc} data-testid="batch-generate-desc" className="text-xs font-semibold text-navy underline">
                GERAR DESCRIÇÃO
              </button>
            </div>
            <textarea id="batch-description" data-testid="batch-description" value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} className="mt-1 w-full rounded-xl border border-input bg-white px-4 py-3 text-sm outline-none focus:border-navy" />
            <div className="mt-3">
              <label htmlFor="batch-status" className={labelCls}>Status inicial de todas as variantes</label>
              <select id="batch-status" data-testid="batch-status-select" value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls}>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
            </div>
            <div className="mt-4 border-t border-navy/10 pt-4" data-testid="batch-cover-ai">
              <CoverAIGenerator
                productName={fabric.name || 'Novo tecido'}
                variants={kept}
                cover={coverAI}
                onApprove={setCoverAI}
                onRemove={() => setCoverAI(null)}
              />
            </div>
          </div>

          <div className="rounded-2xl border border-navy/10 bg-white p-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Variantes ({kept.length})</p>
            <ul className="mt-3 space-y-2" data-testid="batch-review-variants">
              {kept.map((v) => (
                <li key={v.key} className="flex items-center gap-3 rounded-xl bg-navy-light/40 px-3 py-2" data-testid={`batch-review-variant-${v.key}`}>
                  {v.image && <img src={v.image} alt="" className="h-10 w-10 shrink-0 rounded-lg object-cover" />}
                  <span className="min-w-0 flex-1 truncate text-sm font-medium">{v.name}</span>
                  {v.code && <span className="text-xs text-muted-foreground">Cód. {v.code}</span>}
                  <span className="text-xs text-muted-foreground">{v.pattern}</span>
                </li>
              ))}
            </ul>
          </div>

          {pendingDupes.length > 0 && (
            <p className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800" data-testid="batch-review-dupe-block">
              Há alertas de duplicidade sem decisão. Volte à etapa 4 e decida cada um.
            </p>
          )}
          <div className="flex flex-col gap-3 sm:flex-row">
            <button type="button" onClick={() => setStep('fotos')} className="flex items-center justify-center gap-2 rounded-full border border-navy/20 px-6 py-3.5 text-sm font-semibold text-navy hover:bg-navy-light" data-testid="batch-back-fotos">
              <ArrowLeft className="h-4 w-4" /> VOLTAR
            </button>
            <button
              type="button"
              onClick={publish}
              disabled={kept.length === 0 || pendingDupes.length > 0 || pendingInvalid || publishing || (needsDestChoice && attachChoice === null)}
              data-testid="batch-publish-button"
              className={`flex-1 ${nextBtn} sm:flex-none`}
            >
              <Check className="h-4 w-4" /> {publishing ? 'CADASTRANDO…' : `APROVAR E CADASTRAR (${kept.length})`}
            </button>
          </div>
        </section>
      )}


      {suggest && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" data-testid="batch-photo-suggest-modal">
          <div className="w-full max-w-sm rounded-2xl bg-white p-5 text-center">
            <img src={suggest.dataUrl} alt="Foto enviada" className="mx-auto h-40 w-40 rounded-xl object-cover" />
            <p className="mt-3 text-sm font-semibold text-navy">A IA sugere que esta foto é da variante:</p>
            <p className="mt-1 font-serif text-xl text-navy" data-testid="batch-suggest-name">{suggest.name}</p>
            <div className="mt-2">
              <label htmlFor="batch-suggest-alt" className="sr-only">Escolher outra variante</label>
              <select id="batch-suggest-alt" data-testid="batch-suggest-alt" value={suggest.key} onChange={(e) => { const t = kept.find((v) => v.key === e.target.value); setSuggest({ ...suggest, key: t.key, name: t.name }); }} className={inputCls}>
                {kept.map((v) => (
                  <option key={v.key} value={v.key}>{v.name}</option>
                ))}
              </select>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <button type="button" onClick={() => setSuggest(null)} data-testid="batch-photo-cancel" className="rounded-full border border-navy/20 px-4 py-3 text-xs font-bold text-navy">
                CANCELAR
              </button>
              <button
                type="button"
                onClick={() => {
                  patchVariant(suggest.key, { image: suggest.dataUrl, individual: true });
                  setSuggest(null);
                }}
                data-testid="batch-photo-confirm"
                className="rounded-full bg-navy px-4 py-3 text-xs font-bold text-white"
              >
                CONFIRMAR
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
